import { ChangeDetectionStrategy, Component, Injector, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Client, ISizeName } from '@df/core/service/client';
import { GoogleRecaptchaDirective } from '@df/module-google-recaptcha/google-recaptcha.directive';
import { GridColumnDirective } from '@df/ui/atomic/atom/grid/grid-column.directive';
import { GridDirective } from '@df/ui/atomic/atom/grid/grid.directive';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { FormFieldComponent } from 'projects/app-default/src/app/common/form/field/form-field.component';
import { BehaviorSubject } from 'rxjs';
import { CheckboxComponent } from '../../../../..//common/form/checkbox/checkbox.component';
import { FormMuiFieldComponent } from '../../../../..//common/form/mui-field/form-mui-field.component';
import { NModalLayoutRight } from '../../../../..//common/modal/layout-right/modal-layout-right.component';
import { ButtonStyleComponent } from '../../../../../common/buttons/button-style/button-style.component';
import { ResultComponent } from '../../../../../common/result/result.component';
import { AbstractLocalCustomformComponentDef } from '../../../../customform/abstract-local-customform.component.def';
import { CustomformAddressFieldsetComponent } from '../../../../customform/address-fieldset/customform-address-fieldset.component';
import { InlineButtonComponent } from './../../../../../common/buttons/inline-button/inline-button.component';
import { FileInputComponent } from './../../../../../ui/form/file-input/file-input.component';
import { RadioGroupComponent } from 'projects/app-default/src/app/common/form/radio/group/radio-group.component';
import { RadioGroupItemComponent } from 'projects/app-default/src/app/common/form/radio/group/item/radio-group-item.component';

export namespace NProductViewRequestQuoteModal {
    export const BLOCK_ID = 'product-view-request-quote-modal';

    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IBlockDataContent extends NICmsContentComponent.IData {
        title: string;
        form_identifier?: string;
    }
}

@DfCmsModal({
    id: NProductViewRequestQuoteModal.BLOCK_ID,
    name: 'Product View Request Quote Modal',
    position: (sizeName: ISizeName) => (sizeName === 'S' ? 'bottom' : 'right'),
    config: sizeName => ({
        ...NModalLayoutRight.getConfig(sizeName),
        width: NModalLayoutRight.getWidth(sizeName),
        height: sizeName === 'S' ? '85dvh' : '100dvh',
        position:
            sizeName === 'S'
                ? { bottom: '0' }
                : {
                      top: '0',
                      right: '0',
                      bottom: '0'
                  },
        hasBackdrop: true
    }),
    autoCreate: true,
    reopenPolicy: NDfModal.ReopenPolicy.last,
    sizeRestriction: sizeId => (['S', 'M'].includes(sizeId) ? ['S', 'M'] : ['L', 'X']),
    prerenderAllowed: false
})
@ICmsComponent<ProductViewRequestQuoteModalComponent>({
    id: NProductViewRequestQuoteModal.BLOCK_ID,
    block: {
        url: NProductViewRequestQuoteModal.BLOCK_ID,
        autoload: 'bootstrap'
    },
    onDemandOnly: true,
    name: 'Product View Request Quote Modal',
    description: 'Product View Request Quote Modal',
    fields: [
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'Request Quote',
            required: true
        },
        {
            key: 'form_identifier',
            type: NICmsContentField.EFieldType.text,
            title: 'Form Identifier',
            default: 'request_quote_form',
            required: true
        }
    ]
})
@Component({
    selector: 'product-view-request-quote-modal',
    templateUrl: './product-view-request-quote-modal.html',
    styleUrl: './product-view-request-quote-modal.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ICmsClientModule,
        FormMuiFieldComponent,
        FormsModule,
        ButtonStyleComponent,
        ResultComponent,
        RadioGroupComponent,
        RadioGroupItemComponent,
        CheckboxComponent,
        GridDirective,
        GridColumnDirective,
        TranslatePipe,
        CustomformAddressFieldsetComponent,
        FileInputComponent,
        InlineButtonComponent,
        GoogleRecaptchaDirective,
        FormFieldComponent
    ]
})
export class ProductViewRequestQuoteModalComponent
    extends AbstractLocalCustomformComponentDef
    implements NICmsContentComponent.IComponent, OnInit
{
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProductViewRequestQuoteModal.IBlockDataContent;

    _icmsContent$!: BehaviorSubject<any>;

    dummyModel = {};

    readonly injector = inject(Injector);

    private modalRef = inject(ModalRef<ProductViewRequestQuoteModalComponent>);

    protected client = inject(Client);

    protected modalData: (message: string) => void = inject(MODAL_DATA);

    override ngOnInit(): void {
        super.ngOnInit();

        if (this._icmsContent$) {
            this.subscriptions.add(
                this._icmsContent$.subscribe(() => {
                    this.identifier = this.content?.form_identifier as string;
                    this.loadForm();
                })
            );
        }
    }

    protected close() {
        this.modalRef.close();
    }

    protected override onSubmitSuccess(response?: any): void {
        super.onSubmitSuccess(response);
        this.modalData(this.model?.data?.success_message || 'Quote request submitted');
        this.close();
    }
}
